import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, Building2, Plus, Edit, Trash2, UserPlus, UserMinus,
  Percent, DollarSign, Mail, Phone, FileText, Search
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { confirmAsync } from '@/lib/confirm';

export default function Partners({ user }) {
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterEstado, setFilterEstado] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showClientsDialog, setShowClientsDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre_comercial: '',
    tipo: 'distribuidor',
    razon_social: '',
    cif_nif: '',
    direccion_fiscal: '',
    email_facturacion: '',
    telefono: '',
    descuento_partner: 0,
    comisiones: { license: 10, service: 10, equipment: 5 },
    dia_cierre: 0,
    notas: ''
  });
  
  // Clients for assignment
  const [availableClients, setAvailableClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [partnerClients, setPartnerClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');

  useEffect(() => {
    fetchPartners();
    fetchStats();
  }, [filterTipo, filterEstado]);

  const fetchPartners = async () => {
    try {
      let url = '/partners';
      const params = [];
      if (filterTipo !== 'all') params.push(`tipo=${filterTipo}`);
      if (filterEstado !== 'all') params.push(`estado=${filterEstado}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axiosInstance.get(url);
      setPartners(response.data);
    } catch (error) {
      toast.error('Error al cargar partners');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/partners/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAvailableClients = async () => {
    try {
      // Cargar todos los clientes (usar page_size grande para obtener todos)
      const response = await axiosInstance.get('/customers?page_size=2000');
      // La respuesta puede ser un objeto paginado o un array
      const clientsData = response.data.items || response.data;
      // Filtrar clientes que no tienen partner asignado
      const available = clientsData.filter(c => !c.partner_id);
      setAvailableClients(available);
      setFilteredClients(available.slice(0, 50)); // Mostrar solo los primeros 50 inicialmente
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Error al cargar clientes');
    }
  };

  // Filtrar clientes por búsqueda
  const handleClientSearch = (term) => {
    setClientSearchTerm(term);
    if (!term.trim()) {
      setFilteredClients(availableClients.slice(0, 50));
      return;
    }
    const filtered = availableClients.filter(c => 
      c.name?.toLowerCase().includes(term.toLowerCase()) ||
      c.cif?.toLowerCase().includes(term.toLowerCase()) ||
      c.email?.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredClients(filtered.slice(0, 50));
  };

  // Toggle selección de cliente
  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const fetchPartnerClients = async (partnerId) => {
    try {
      const response = await axiosInstance.get(`/partners/${partnerId}/clients?include_historical=true`);
      setPartnerClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes del partner');
    }
  };

  const handleCreate = async () => {
    try {
      await axiosInstance.post('/partners', formData);
      toast.success('Partner creado correctamente');
      setShowCreateDialog(false);
      resetForm();
      fetchPartners();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear partner');
    }
  };

  const handleUpdate = async () => {
    try {
      await axiosInstance.put(`/partners/${selectedPartner.id}`, formData);
      toast.success('Partner actualizado correctamente');
      setShowEditDialog(false);
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar partner');
    }
  };

  const handleDelete = async (partnerId) => {
    if (!(await confirmAsync('¿Estás seguro de eliminar este partner?'))) return;
    
    try {
      await axiosInstance.delete(`/partners/${partnerId}`);
      toast.success('Partner eliminado correctamente');
      fetchPartners();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar partner');
    }
  };

  const handleAssignClients = async () => {
    if (selectedClients.length === 0) {
      toast.error('Selecciona al menos un cliente');
      return;
    }
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const clientId of selectedClients) {
      try {
        await axiosInstance.post(`/partners/${selectedPartner.id}/assign-client?client_id=${clientId}`);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} cliente(s) asignado(s) correctamente`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} cliente(s) no se pudieron asignar`);
    }
    
    setShowAssignDialog(false);
    setSelectedClients([]);
    setClientSearchTerm('');
    fetchPartners();
    fetchStats();
  };

  const handleUnassignClient = async (clientId) => {
    if (!(await confirmAsync('¿Estás seguro de desasignar este cliente?'))) return;
    
    try {
      await axiosInstance.post(`/partners/${selectedPartner.id}/unassign-client/${clientId}`);
      toast.success('Cliente desasignado correctamente');
      fetchPartnerClients(selectedPartner.id);
      fetchPartners();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desasignar cliente');
    }
  };

  const openEditDialog = (partner) => {
    setSelectedPartner(partner);
    setFormData({
      nombre_comercial: partner.nombre_comercial,
      tipo: partner.tipo,
      razon_social: partner.razon_social,
      cif_nif: partner.cif_nif,
      direccion_fiscal: partner.direccion_fiscal || '',
      email_facturacion: partner.email_facturacion,
      telefono: partner.telefono || '',
      descuento_partner: partner.descuento_partner || 0,
      comisiones: partner.comisiones || { license: 10, service: 10, equipment: 5 },
      dia_cierre: partner.dia_cierre || 0,
      notas: partner.notas || ''
    });
    setShowEditDialog(true);
  };

  const openAssignDialog = (partner) => {
    setSelectedPartner(partner);
    setSelectedClients([]);
    setClientSearchTerm('');
    fetchAvailableClients();
    setShowAssignDialog(true);
  };

  const openClientsDialog = (partner) => {
    setSelectedPartner(partner);
    fetchPartnerClients(partner.id);
    setShowClientsDialog(true);
  };

  const resetForm = () => {
    setFormData({
      nombre_comercial: '',
      tipo: 'distribuidor',
      razon_social: '',
      cif_nif: '',
      direccion_fiscal: '',
      email_facturacion: '',
      telefono: '',
      descuento_partner: 0,
      comisiones: { license: 10, service: 10, equipment: 5 },
      dia_cierre: 0,
      notas: ''
    });
  };

  const filteredPartners = partners.filter(p => 
    p.nombre_comercial.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.razon_social.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.cif_nif.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Partners</h1>
            <p className="text-gray-500">Gestión de distribuidores y prescriptores</p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="bg-[#9C0F17]">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Partner
          </Button>
        </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Partners</p>
                  <p className="text-2xl font-bold">{stats.total_partners}</p>
                </div>
                <Building2 className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Activos</p>
                  <p className="text-2xl font-bold text-green-600">{stats.activos}</p>
                </div>
                <Badge className="bg-green-100 text-green-800">Activo</Badge>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Distribuidores</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.distribuidores}</p>
                </div>
                <Badge className="bg-blue-100 text-blue-800">DIST</Badge>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Prescriptores</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.prescriptores}</p>
                </div>
                <Badge className="bg-purple-100 text-purple-800">PRESC</Badge>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Clientes asignados</p>
                  <p className="text-2xl font-bold">{stats.clientes_con_partner}</p>
                </div>
                <Users className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por nombre, razón social o CIF..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="distribuidor">Distribuidores</SelectItem>
                <SelectItem value="prescriptor">Prescriptores</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterEstado} onValueChange={setFilterEstado}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="activo">Activos</SelectItem>
                <SelectItem value="inactivo">Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Partners List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPartners.map((partner) => (
          <Card key={partner.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{partner.nombre_comercial}</CardTitle>
                  <p className="text-sm text-gray-500">{partner.razon_social}</p>
                </div>
                <div className="flex gap-1">
                  <Badge className={partner.tipo === 'distribuidor' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                    {partner.tipo === 'distribuidor' ? 'DISTRIBUIDOR' : 'PRESCRIPTOR'}
                  </Badge>
                  <Badge className={partner.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {partner.estado}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm space-y-1">
                <p><span className="text-gray-500">CIF:</span> {partner.cif_nif}</p>
                <p className="flex items-center gap-1">
                  <Mail className="w-3 h-3 text-gray-400" />
                  {partner.email_facturacion}
                </p>
                {partner.telefono && (
                  <p className="flex items-center gap-1">
                    <Phone className="w-3 h-3 text-gray-400" />
                    {partner.telefono}
                  </p>
                )}
              </div>
              
              {/* Condiciones económicas */}
              <div className="bg-gray-50 rounded p-2 text-sm">
                {partner.tipo === 'distribuidor' ? (
                  <div className="flex items-center gap-2">
                    <Percent className="w-4 h-4 text-blue-500" />
                    <span>Descuento: <strong>{partner.descuento_partner}%</strong></span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="font-medium flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-purple-500" />
                      Comisiones:
                    </p>
                    <div className="grid grid-cols-3 gap-1 text-xs">
                      <span>Lic: {partner.comisiones?.license || 0}%</span>
                      <span>Serv: {partner.comisiones?.service || 0}%</span>
                      <span>Eq: {partner.comisiones?.equipment || 0}%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Clientes */}
              <div 
                className="flex items-center justify-between p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100"
                onClick={() => openClientsDialog(partner)}
              >
                <span className="text-sm flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  Clientes activos
                </span>
                <Badge variant="outline">{partner.clientes_activos || 0}</Badge>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => openEditDialog(partner)}>
                  <Edit className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                <Button variant="outline" size="sm" onClick={() => openAssignDialog(partner)}>
                  <UserPlus className="w-4 h-4 mr-1" />
                  Asignar
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(partner.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPartners.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No hay partners registrados</p>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => { if (!open) { setShowCreateDialog(false); setShowEditDialog(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Editar Partner' : 'Nuevo Partner'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre Comercial *</Label>
                <Input
                  value={formData.nombre_comercial}
                  onChange={(e) => setFormData({...formData, nombre_comercial: e.target.value})}
                />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="distribuidor">Distribuidor</SelectItem>
                    <SelectItem value="prescriptor">Prescriptor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Razón Social *</Label>
                <Input
                  value={formData.razon_social}
                  onChange={(e) => setFormData({...formData, razon_social: e.target.value})}
                />
              </div>
              <div>
                <Label>CIF/NIF *</Label>
                <Input
                  value={formData.cif_nif}
                  onChange={(e) => setFormData({...formData, cif_nif: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Dirección Fiscal</Label>
              <Input
                value={formData.direccion_fiscal}
                onChange={(e) => setFormData({...formData, direccion_fiscal: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email Facturación *</Label>
                <Input
                  type="email"
                  value={formData.email_facturacion}
                  onChange={(e) => setFormData({...formData, email_facturacion: e.target.value})}
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                />
              </div>
            </div>

            {/* Condiciones económicas según tipo */}
            <div className="border rounded-lg p-4 space-y-3">
              <h3 className="font-medium">Condiciones Económicas</h3>
              
              {formData.tipo === 'distribuidor' ? (
                <div>
                  <Label>Descuento sobre precio normal (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.descuento_partner}
                    onChange={(e) => setFormData({...formData, descuento_partner: parseFloat(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">El partner pagará el precio normal menos este descuento</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500">Porcentaje de comisión por categoría de producto:</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Licencias (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.comisiones.license}
                        onChange={(e) => setFormData({
                          ...formData, 
                          comisiones: {...formData.comisiones, license: parseFloat(e.target.value) || 0}
                        })}
                      />
                    </div>
                    <div>
                      <Label>Servicios (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.comisiones.service}
                        onChange={(e) => setFormData({
                          ...formData, 
                          comisiones: {...formData.comisiones, service: parseFloat(e.target.value) || 0}
                        })}
                      />
                    </div>
                    <div>
                      <Label>Equipos (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.comisiones.equipment}
                        onChange={(e) => setFormData({
                          ...formData, 
                          comisiones: {...formData.comisiones, equipment: parseFloat(e.target.value) || 0}
                        })}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label>Notas</Label>
              <Input
                value={formData.notas}
                onChange={(e) => setFormData({...formData, notas: e.target.value})}
                placeholder="Notas adicionales..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }}>
              Cancelar
            </Button>
            <Button onClick={showEditDialog ? handleUpdate : handleCreate} className="bg-[#9C0F17]">
              {showEditDialog ? 'Guardar Cambios' : 'Crear Partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Client Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Asignar Clientes a {selectedPartner?.nombre_comercial}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Buscador */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar por nombre, CIF o email..."
                value={clientSearchTerm}
                onChange={(e) => handleClientSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Info de selección */}
            {selectedClients.length > 0 && (
              <div className="bg-blue-50 text-blue-800 px-3 py-2 rounded-lg text-sm flex items-center justify-between">
                <span>{selectedClients.length} cliente(s) seleccionado(s)</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedClients([])}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Limpiar selección
                </Button>
              </div>
            )}

            {/* Lista de clientes */}
            <div className="border rounded-lg max-h-[400px] overflow-y-auto">
              {filteredClients.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {clientSearchTerm ? 'No se encontraron clientes' : 'No hay clientes disponibles sin partner'}
                </p>
              ) : (
                filteredClients.map(client => (
                  <div 
                    key={client.id}
                    onClick={() => toggleClientSelection(client.id)}
                    className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedClients.includes(client.id) ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <input 
                      type="checkbox" 
                      checked={selectedClients.includes(client.id)}
                      onChange={() => {}}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{client.name}</p>
                      <p className="text-sm text-gray-500">{client.cif} • {client.email}</p>
                    </div>
                    {client.status === 'baja' && (
                      <Badge variant="outline" className="text-gray-500">Baja</Badge>
                    )}
                  </div>
                ))
              )}
            </div>
            
            {availableClients.length > 50 && (
              <p className="text-xs text-gray-500 text-center">
                Mostrando {filteredClients.length} de {availableClients.length} clientes disponibles. Usa el buscador para encontrar más.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancelar</Button>
            <Button 
              onClick={handleAssignClients} 
              className="bg-[#9C0F17]"
              disabled={selectedClients.length === 0}
            >
              Asignar {selectedClients.length > 0 ? `(${selectedClients.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner Clients Dialog */}
      <Dialog open={showClientsDialog} onOpenChange={setShowClientsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Clientes de {selectedPartner?.nombre_comercial}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {partnerClients.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No hay clientes asignados</p>
            ) : (
              partnerClients.map(assignment => (
                <div 
                  key={assignment.id} 
                  className={`flex items-center justify-between p-3 rounded-lg border ${assignment.fecha_fin ? 'bg-gray-50 opacity-60' : 'bg-white'}`}
                >
                  <div>
                    <p className="font-medium">{assignment.client_name}</p>
                    <p className="text-sm text-gray-500">
                      Desde: {new Date(assignment.fecha_inicio).toLocaleDateString()}
                      {assignment.fecha_fin && ` - Hasta: ${new Date(assignment.fecha_fin).toLocaleDateString()}`}
                    </p>
                    {assignment.client_data && (
                      <p className="text-xs text-gray-400">{assignment.client_data.cif}</p>
                    )}
                  </div>
                  {!assignment.fecha_fin && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600"
                      onClick={() => handleUnassignClient(assignment.client_id)}
                    >
                      <UserMinus className="w-4 h-4 mr-1" />
                      Desasignar
                    </Button>
                  )}
                  {assignment.fecha_fin && (
                    <Badge variant="outline" className="text-gray-500">Histórico</Badge>
                  )}
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientsDialog(false)}>Cerrar</Button>
            <Button onClick={() => { setShowClientsDialog(false); openAssignDialog(selectedPartner); }} className="bg-[#9C0F17]">
              <UserPlus className="w-4 h-4 mr-2" />
              Asignar Nuevo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}
